import { useCallback } from 'react';

import {
  signInWithRedirect,
  signOut as signOutCognito,
  getCurrentUser,
  toAuthTokens,
  waitForSession,
} from '@core/auth/helpers/configure';

import { UseAuthType } from '@core/auth/interfaces';

// Cognito managed login supports only these languages (Essentials/Plus plan required).
// Source: https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-user-pools-managed-login.html
const COGNITO_SUPPORTED_LANGS = new Set([
  'de',
  'en',
  'es',
  'fr',
  'it',
  'ja',
  'ko',
  'nl',
  'pt',
  'zh-cn',
]);

// App codes that don't match Cognito's expected value directly.
const COGNITO_LOCALE_OVERRIDES: Record<string, string> = {
  pt: 'pt-BR',
  'zh-cn': 'zh-CN',
};

/**
 * Exposes the Cognito sign-in, sign-out, identity and token calls the provider
 * needs, over `oidc-client-ts`.
 *
 * @returns `signIn`, `signOut`, `getAuthTokens` and `authUser`.
 */
const useAuth: UseAuthType = () => {
  const signIn = useCallback(async (language?: string) => {
    const lang = language?.toLowerCase();
    const cognitoLocale =
      lang && COGNITO_SUPPORTED_LANGS.has(lang)
        ? COGNITO_LOCALE_OVERRIDES[lang] ?? lang
        : undefined;

    // Appended as `&lang=<locale>` to the /oauth2/authorize request, which
    // Cognito passes through to the hosted UI login page.
    await signInWithRedirect({ lang: cognitoLocale });
  }, []);

  const signOut = useCallback(async () => {
    await signOutCognito();
  }, []);

  const authUser = useCallback(async () => {
    return await getCurrentUser();
  }, []);

  // The Amplify version slept 1s here (e683ec522, raised from 100ms) to let it
  // finish the post-redirect `?code=` exchange it ran in the background —
  // called any earlier, `fetchAuthSession` returned no tokens and the app fell
  // through to the login screen. `AuthProvider`'s `OidcSessionGate` now holds
  // this hook's consumers back until react-oidc-context has completed that
  // exchange, so the session is already stored by the time anything calls this
  // and the sleep is gone. `waitForSession` keeps a bounded retry as a safety
  // net for any path that still reads tokens mid-exchange, without the flat 1s
  // cost on every call.
  const getTokens = useCallback(async (options: any = {}) => {
    return toAuthTokens(await waitForSession(options));
  }, []);

  return { signIn, signOut, getAuthTokens: getTokens, authUser };
};

export default useAuth;
